import { createFileRoute, Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';

import { isPermissionDenied } from '../../../../../api/call.js';
import { useCustomPages } from '../../../../../api/custom-pages.js';
import { Button } from '../../../../../components/Button.js';
import buttonStyles from '../../../../../components/button.module.css';
import { DangerAlert } from '../../../../../components/DangerAlert.js';
import { PageHeading } from '../../../../../components/PageHeading.js';
import { LoadingArea } from '../../../../../components/PlaceholderPage.js';
import { PermissionDeniedPage } from '../../../../../components/SectionStates.js';
import { formatListTime } from '../../../../../conversations/display.js';
import styles from '../../../../../custom-data/custom-data.module.css';

/** `/websites/:websiteId/custom-data`: the website's custom pages, oldest first. */
export const Route = createFileRoute('/_app/websites/$websiteId/custom-data/')({
  component: CustomDataList,
});

const TITLE = 'Custom data';

function CustomDataList() {
  const { websiteId } = Route.useParams();
  const list = useCustomPages(websiteId);
  const now = new Date();

  if (list.isError && isPermissionDenied(list.error)) {
    return <PermissionDeniedPage title={TITLE} permission="knowledge" />;
  }

  const limit = list.data?.limit ?? null;
  const used = list.data?.items.length ?? 0;
  const full = limit !== null && used >= limit;

  // A link, because it opens a page; it looks like the primary button. At the
  // plan's limit it is a disabled button with the reason under it.
  const addButton = full ? (
    <div className={styles.addFull}>
      <Button aria-disabled="true" aria-describedby="custom-pages-limit">
        <Plus aria-hidden="true" width={16} height={16} strokeWidth={1.75} />
        Add custom data
      </Button>
      <p id="custom-pages-limit" className={styles.limitReason}>
        {limit === 0
          ? 'Custom data is included on paid plans. Move this website to a paid plan in Settings to add custom pages.'
          : `Your plan allows ${limit} custom ${limit === 1 ? 'page' : 'pages'}. Delete one to add another.`}
      </p>
    </div>
  ) : (
    <Link
      className={`${buttonStyles.button} ${buttonStyles.primary}`}
      to="/websites/$websiteId/custom-data/new"
      params={{ websiteId }}
    >
      <Plus aria-hidden="true" width={16} height={16} strokeWidth={1.75} />
      Add custom data
    </Link>
  );

  return (
    <>
      <div className={styles.header}>
        <div>
          <PageHeading documentTitle={TITLE}>{TITLE}</PageHeading>
          <p className={styles.caption}>
            Information the assistant may use that isn&apos;t on the website, such as parking,
            emergency appointments or what to bring. It stays in Groundly and has no public link.
          </p>
          {limit !== null && limit > 0 && list.data ? (
            <p className={styles.caption} data-testid="custom-pages-allowance">
              {used} of {limit} custom {limit === 1 ? 'page' : 'pages'} used · up to 5,000
              characters each
            </p>
          ) : null}
        </div>
        {list.data && list.data.items.length > 0 ? addButton : null}
      </div>

      {list.isError ? (
        <DangerAlert
          title="Couldn't load custom data"
          action={<Button onClick={() => void list.refetch()}>Try again</Button>}
        >
          Check your connection and try again.
        </DangerAlert>
      ) : list.isPending ? (
        <LoadingArea />
      ) : list.data.items.length === 0 ? (
        <div className={styles.empty}>
          <h2>No custom data yet</h2>
          <p>
            Write information the assistant may use that isn&apos;t on the website. It&apos;s
            knowledge as soon as you save it.
          </p>
          {addButton}
        </div>
      ) : (
        <ul className={styles.list} aria-label="Custom pages">
          {list.data.items.map((page) => (
            <li key={page.id}>
              <Link
                className={styles.item}
                to="/websites/$websiteId/custom-data/$customPageId"
                params={{ websiteId, customPageId: page.id }}
              >
                <span className={styles.itemTitle}>{page.title}</span>
                <p className={styles.itemExcerpt}>{page.excerpt}</p>
                <p className={styles.itemMeta}>Updated {formatListTime(page.updatedAt, now)}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
